import React from 'react';
import { BarChart3, TrendingUp, Users, Target, MousePointer2 } from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  PieChart, Pie, Cell, LineChart, Line 
} from 'recharts';

const retentionData = [
  { day: 'Day 1', rate: 100 },
  { day: 'Day 3', rate: 65 },
  { day: 'Day 7', rate: 42 },
  { day: 'Day 14', rate: 28 },
  { day: 'Day 30', rate: 15 },
];

const featureUsage = [
  { name: 'Studio Sync', value: 400 },
  { name: 'UI Gen', value: 300 },
  { name: 'Melee Logic', value: 200 },
  { name: 'Data Stores', value: 150 },
];

const COLORS = ['#16a34a', '#3b82f6', '#8b5cf6', '#f59e0b'];

const Analytics = () => {
  return (
    <div className="space-y-8 max-w-7xl mx-auto">
      <section>
        <h1 className="text-2xl font-syne font-bold">Platform Analytics</h1>
        <p className="text-sonas-muted text-xs mt-1">Deep dive into user behavior and feature performance.</p>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-sonas-surface border border-sonas-border rounded-2xl p-6">
          <h3 className="font-syne font-bold mb-8 flex items-center gap-2">
            <TrendingUp size={18} className="text-sonas-accent" />
            User Retention Curve
          </h3>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={retentionData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#222" vertical={false} />
                <XAxis dataKey="day" stroke="#666" fontSize={10} tickLine={false} axisLine={false} />
                <YAxis stroke="#666" fontSize={10} tickLine={false} axisLine={false} unit="%" />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#0a0a0a', border: '1px solid #222', borderRadius: '12px' }}
                />
                <Line type="monotone" dataKey="rate" stroke="#16a34a" strokeWidth={3} dot={{ fill: '#16a34a', r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-sonas-surface border border-sonas-border rounded-2xl p-6">
          <h3 className="font-syne font-bold mb-8 flex items-center gap-2">
            <Target size={18} className="text-blue-500" />
            Popular Feature Distribution
          </h3>
          <div className="h-[300px] w-full flex items-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={featureUsage}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {featureUsage.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ backgroundColor: '#0a0a0a', border: '1px solid #222', borderRadius: '12px' }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-4 pr-8">
              {featureUsage.map((item, i) => (
                <div key={item.name} className="flex items-center gap-3">
                  <div className="h-3 w-3 rounded-full" style={{ backgroundColor: COLORS[i] }} />
                  <span className="text-[10px] font-bold uppercase tracking-widest text-sonas-dim">{item.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[
          { label: 'Avg. Prompt Length', value: '42 Words', icon: MousePointer2 },
          { label: 'DAU / MAU Ratio', value: '18.4%', icon: Users },
          { label: 'Sync Success Rate', value: '94.2%', icon: BarChart3 },
        ].map((item, i) => (
          <div key={i} className="bg-sonas-surface2 border border-sonas-border p-6 rounded-2xl space-y-2">
            <div className="flex items-center gap-2 text-sonas-dim mb-2">
              <item.icon size={16} />
              <span className="text-[10px] font-bold uppercase tracking-widest">{item.label}</span>
            </div>
            <p className="text-2xl font-syne font-bold">{item.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Analytics;